import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  API_BASE,
  CONTACT_COMPANY,
  CONTACT_ADDRESS,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
  CONTACT_WHATSAPP_HREF,
  CONTACT_SLA,
  CONTACT_MAP_EMBED
} from '../config';
import PublicHeader from '../components/Header/PublicHeader';
import PublicFooter from '../components/Footer/PublicFooter';
import Button from '../components/ui/Button';
import {
  CheckCircle2,
  Building2,
  Mail,
  User,
  Phone,
  MapPin,
  Clock,
  Send,
  Layers,
  Users,
  MessageSquare
} from 'lucide-react';
import './BookDemo.css';

const BookDemo = () => {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    company: '',
    productInterest: 'Vaultize Zero-Trust Data Privacy Vault & PCI/HIPAA Tokenization',
    scaleSize: '11 - 50 Users / Nodes',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    try {
      const res = await fetch(`${API_BASE}/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.name,
          workEmail: formData.email,
          companyName: formData.company || 'Direct Enterprise Inquiry',
          phoneNumber: formData.phone,
          jobRole: 'Enterprise Contact',
          companySize: formData.scaleSize,
          primaryUseCase: formData.productInterest,
          estimatedMonthlyTokens: formData.scaleSize,
          message: formData.notes,
          source: 'CONTACT_PAGE'
        })
      });

      if (!res.ok) {
        throw new Error('Failed to submit inquiry');
      }

      const data = await res.json();
      setRefCode(data.referenceCode || 'VZ-INQUIRY-CONFIRMED');
      setSubmitted(true);
    } catch (err) {
      console.error('[Contact/BookDemo] submit error', err);
      setErrorMsg(`Unable to submit request right now. Please email us directly at ${CONTACT_EMAIL} or call/WhatsApp ${CONTACT_PHONE}.`);
    } finally {
      setLoading(false);
    }
  };

  const handleTryDashboard = async () => {
    const data = await refreshProfile();
    if (data) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <PublicHeader />
      <main className="contact-page-wrapper">
        <div className="contact-page-bg-glow" />

        {/* Page Hero Header (Matching krydacloud.com/contact) */}
        <div className="container contact-hero-header">
          <span className="contact-hero-badge">
            Enterprise Inquiries &amp; Architecture Desk
          </span>
          <h1 className="contact-hero-title">
            Initiate an <span className="contact-gradient-text">Enterprise Consultation</span>
          </h1>
          <p className="contact-hero-subtitle">
            Connect directly with our cloud solutions architects to scope your infrastructure, schedule a live product walkthrough, or discuss custom tokenization and privacy guardrails.
          </p>
        </div>

        <div className="container contact-grid-layout">
          {/* Left Column: Corporate Contact Info + Map */}
          <div className="contact-info-column">
            <div className="contact-corporate-card">
              <h3 className="contact-card-heading">Corporate Contact</h3>

              <div className="contact-detail-list">
                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <MapPin size={18} />
                  </div>
                  <div className="contact-detail-body">
                    <span className="contact-detail-label">Registered Office</span>
                    <strong className="contact-detail-primary">{CONTACT_COMPANY}</strong>
                    <span className="contact-detail-secondary">{CONTACT_ADDRESS}</span>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Mail size={18} />
                  </div>
                  <div className="contact-detail-body">
                    <span className="contact-detail-label">Official Business Inquiries</span>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="contact-detail-link">
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Phone size={18} />
                  </div>
                  <div className="contact-detail-body">
                    <span className="contact-detail-label">Direct Telephone / WhatsApp</span>
                    <div className="contact-phone-links">
                      <a href={CONTACT_PHONE_HREF} className="contact-detail-link">
                        {CONTACT_PHONE}
                      </a>
                      <a
                        href={CONTACT_WHATSAPP_HREF}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-whatsapp-pill"
                      >
                        WhatsApp Direct
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Clock size={18} />
                  </div>
                  <div className="contact-detail-body">
                    <span className="contact-detail-label">Response SLA</span>
                    <span className="contact-detail-secondary">{CONTACT_SLA}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Embedded Google Map */}
            <div className="contact-map-card">
              <iframe
                src={CONTACT_MAP_EMBED}
                width="100%"
                height="260"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Kryda Cloud Registered Office - Noida, UP"
              />
            </div>
          </div>

          {/* Right Column: Submit an Inquiry or Project Scope */}
          <div className="contact-form-column">
            <div className="demo-card contact-form-card">
              {!submitted ? (
                <>
                  <div className="contact-form-header">
                    <h2 className="contact-form-title">Submit an Inquiry or Project Scope</h2>
                    <p className="contact-form-subtitle">
                      Complete the form below and an engineering lead will respond within 4 business hours.
                    </p>
                  </div>

                  {errorMsg && (
                    <div className="demo-error-alert">
                      {errorMsg}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="demo-form">
                    <div className="contact-form-row-2">
                      <div className="form-group">
                        <label htmlFor="demo-name">
                          <User size={14} className="inline-icon" /> Full Name *
                        </label>
                        <input
                          id="demo-name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="e.g. Vikram Malhotra"
                          className="input"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="demo-email">
                          <Mail size={14} className="inline-icon" /> Business Email *
                        </label>
                        <input
                          id="demo-email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="vikram@enterprise.com"
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="contact-form-row-2">
                      <div className="form-group">
                        <label htmlFor="demo-phone">
                          <Phone size={14} className="inline-icon" /> Phone / WhatsApp Number
                        </label>
                        <input
                          id="demo-phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 79055 45978"
                          className="input"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="demo-company">
                          <Building2 size={14} className="inline-icon" /> Company / Outlet Name
                        </label>
                        <input
                          id="demo-company"
                          type="text"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder="e.g. Apex Global Outlets"
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="contact-form-row-2">
                      <div className="form-group">
                        <label htmlFor="demo-interest">
                          <Layers size={14} className="inline-icon" /> Product Interest *
                        </label>
                        <select
                          id="demo-interest"
                          value={formData.productInterest}
                          onChange={(e) => setFormData({ ...formData, productInterest: e.target.value })}
                          className="input select-input"
                        >
                          <option value="Vaultize Zero-Trust Data Privacy Vault & PCI/HIPAA Tokenization">
                            Vaultize Data Privacy Vault &amp; PCI/HIPAA Tokenization
                          </option>
                          <option value="Vaultize GenAI & LLM Privacy Gateway">
                            Vaultize GenAI &amp; LLM Privacy Gateway
                          </option>
                          <option value="Cloud Engineering & DevOps">
                            Cloud Engineering &amp; DevOps
                          </option>
                          <option value="Custom Software & App Development">
                            Custom Software &amp; App Development
                          </option>
                          <option value="AI, Machine Learning & Automation">
                            AI, Machine Learning &amp; Automation
                          </option>
                          <option value="Data & Modern Analytics">
                            Data &amp; Modern Analytics
                          </option>
                          <option value="Comprehensive Enterprise Consulting">
                            Comprehensive Enterprise Consulting
                          </option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="demo-scale">
                          <Users size={14} className="inline-icon" /> Scale / Fleet Size
                        </label>
                        <select
                          id="demo-scale"
                          value={formData.scaleSize}
                          onChange={(e) => setFormData({ ...formData, scaleSize: e.target.value })}
                          className="input select-input"
                        >
                          <option value="1 - 10 Users / Nodes">1 - 10 Users / Nodes</option>
                          <option value="11 - 50 Users / Nodes">11 - 50 Users / Nodes</option>
                          <option value="51 - 200 Enterprise Nodes">51 - 200 Enterprise Nodes</option>
                          <option value="200+ Large Scale Fleet">200+ Large Scale Fleet</option>
                          <option value="Custom Architecture Scope">Custom Architecture Scope</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="demo-notes">
                        <MessageSquare size={14} className="inline-icon" /> Message / Project Scope *
                      </label>
                      <textarea
                        id="demo-notes"
                        rows={4}
                        required
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Describe your operational requirements, integration targets, compliance goals (PCI-DSS, HIPAA, DPDP), or timeline..."
                        className="input textarea"
                      />
                    </div>

                    <div className="demo-actions">
                      <Button type="submit" variant="primary" disabled={loading}>
                        <Send size={15} style={{ marginRight: 6 }} />
                        {loading ? 'Transmitting Request...' : 'Transmit Inbound Request'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleTryDashboard}
                      >
                        Launch Free Trial Sandbox
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="demo-success">
                  <div className="demo-success-icon-wrap">
                    <CheckCircle2 size={48} className="text-cyan" />
                  </div>
                  <h2>Consultation Inquiry Confirmed</h2>
                  <p>
                    Thank you! An engineering architect from {CONTACT_COMPANY} has been assigned to your request.
                  </p>

                  <div className="demo-ref-pill">
                    <span>Reference Code:</span>
                    <strong>{refCode}</strong>
                  </div>

                  <p className="demo-sub-confirm">
                    We have recorded your inquiry under <strong>{formData.email}</strong>. {CONTACT_SLA}
                  </p>

                  <div className="demo-actions" style={{ justifyContent: 'center' }}>
                    <Button variant="primary" onClick={() => navigate('/plans')}>
                      View Pricing &amp; Plans
                    </Button>
                    <Button variant="secondary" onClick={handleTryDashboard}>
                      Open Free Trial Workspace
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </>
  );
};

export default BookDemo;
